/**
 * ClientServicePricingSection — SystemAdmin per-org pricing per service offering
 * (NMVTIS, VIN Decode, Theft/NVS, MVR). One row per offering: the standard base price,
 * an optional org-specific price override, and an optional discount %. The effective
 * price ("org price, then discount") is previewed live. Full-set save: only offerings
 * with a custom price or discount are persisted.
 */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Tag, Loader2, RefreshCw, Save, ChevronDown } from 'lucide-react';
import { getErrorMessage } from '../../services/apiService';
import {
  listClientServicePrices, saveClientServicePrices,
  ClientServicePrice, ServicePriceItem,
} from '../../services/clientsAdminService';

interface RowEdit {
  orgPrice: string;        // raw input; '' = use standard base
  discountPercent: string; // raw input; '' or '0' = no discount
  isActive: boolean;
}

const money = (n: number) => `$${n.toFixed(2)}`;

const computeEffective = (base: number, orgPriceRaw: string, discountRaw: string): number => {
  const op = orgPriceRaw.trim() === '' ? base : parseFloat(orgPriceRaw);
  const price = Number.isFinite(op) ? op : base;
  const d = discountRaw.trim() === '' ? 0 : parseFloat(discountRaw);
  const disc = Number.isFinite(d) ? d : 0;
  const eff = price * (1 - disc / 100);
  return Math.max(0, Math.round(eff * 100) / 100);
};

const toEdits = (rows: ClientServicePrice[]): Record<string, RowEdit> => {
  const out: Record<string, RowEdit> = {};
  for (const r of rows) {
    out[r.serviceType] = {
      orgPrice: r.orgPrice == null ? '' : r.orgPrice.toFixed(2),
      discountPercent: r.discountPercent == null || r.discountPercent === 0 ? '' : String(r.discountPercent),
      isActive: r.isActive,
    };
  }
  return out;
};

const ClientServicePricingSection: React.FC<{ clientId: string; defaultOpen?: boolean }> = ({ clientId, defaultOpen = true }) => {
  const [open, setOpen] = useState(defaultOpen);
  const [rows, setRows] = useState<ClientServicePrice[]>([]);
  const [edits, setEdits] = useState<Record<string, RowEdit>>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const data = await listClientServicePrices(clientId);
      setRows(data);
      setEdits(toEdits(data));
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  useEffect(() => { load(); }, [load]);

  const setEdit = (type: string, patch: Partial<RowEdit>) =>
    setEdits((p) => ({ ...p, [type]: { ...p[type], ...patch } }));

  const dirty = useMemo(() => {
    const base = toEdits(rows);
    return rows.some((r) => {
      const a = base[r.serviceType], b = edits[r.serviceType];
      if (!b) return false;
      return a.orgPrice !== b.orgPrice || a.discountPercent !== b.discountPercent || a.isActive !== b.isActive;
    });
  }, [rows, edits]);

  const handleSave = async () => {
    for (const r of rows) {
      const e = edits[r.serviceType];
      if (!e) continue;
      if (e.orgPrice.trim() !== '') {
        const v = parseFloat(e.orgPrice);
        if (!Number.isFinite(v) || v < 0) { setError(`Price for ${r.label} must be 0 or more.`); return; }
      }
      if (e.discountPercent.trim() !== '') {
        const v = parseFloat(e.discountPercent);
        if (!Number.isFinite(v) || v < 0 || v > 100) { setError(`Discount for ${r.label} must be 0–100.`); return; }
      }
    }

    const items: ServicePriceItem[] = [];
    for (const r of rows) {
      const e = edits[r.serviceType];
      if (!e) continue;
      const hasPrice = e.orgPrice.trim() !== '';
      const hasDisc = e.discountPercent.trim() !== '' && parseFloat(e.discountPercent) > 0;
      if (!hasPrice && !hasDisc) continue;
      items.push({
        serviceType: r.serviceType,
        orgPrice: hasPrice ? parseFloat(e.orgPrice) : null,
        discountPercent: hasDisc ? parseFloat(e.discountPercent) : null,
        isActive: e.isActive,
      });
    }

    setSaving(true); setError('');
    try {
      const updated = await saveClientServicePrices(clientId, items);
      setRows(updated);
      setEdits(toEdits(updated));
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-white rounded-2xl border border-black/[0.08] shadow-sm overflow-hidden">
      <div
        onClick={() => setOpen((o) => !o)}
        className={`flex items-center justify-between px-6 py-4 bg-black/[0.02] hover:bg-black/[0.04] transition-colors cursor-pointer ${open ? 'border-b border-black/[0.08]' : ''}`}
      >
        <div className="flex items-center gap-2">
          <ChevronDown className={`w-4 h-4 text-[#94a3b8] transition-transform ${open ? '' : '-rotate-90'}`} />
          <h2 className="text-sm font-semibold uppercase tracking-wider text-[#475569] flex items-center gap-2">
            <Tag className="w-4 h-4 text-[#c41e3a]" /> Service Pricing
          </h2>
        </div>
        <button onClick={(e) => { e.stopPropagation(); load(); }} className="text-xs text-[#64748b] hover:text-[#c41e3a] inline-flex items-center gap-1">
          <RefreshCw className="w-3.5 h-3.5" /> Refresh
        </button>
      </div>

      {open && (
      <div className="px-6 py-5">
      <p className="text-xs text-[#64748b] mb-4">
        Org-specific price per offering, then an optional discount. Leave a price blank to use the standard price shown under it.
        For MVR, any per-state price set above takes precedence over this default.
      </p>

      {error && <div className="mb-4 p-3 rounded-lg border bg-red-50 border-red-200 text-sm text-red-800">{error}</div>}

      {loading ? (
        <div className="py-8 text-center text-[#64748b]"><Loader2 className="w-5 h-5 animate-spin inline mr-2" /> Loading pricing…</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-xs uppercase tracking-wider text-[#94a3b8] border-b border-black/[0.06]">
              <tr>
                <th className="text-left py-2">Offering</th>
                <th className="text-right">Price</th>
                <th className="text-right">Discount %</th>
                <th className="text-right">Effective</th>
                <th className="text-center">Active</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const e = edits[r.serviceType] ?? { orgPrice: '', discountPercent: '', isActive: true };
                const eff = computeEffective(r.standardBasePrice, e.orgPrice, e.discountPercent);
                const custom = e.orgPrice.trim() !== '' || (e.discountPercent.trim() !== '' && e.discountPercent.trim() !== '0');
                return (
                  <tr key={r.serviceType} className="border-b border-black/[0.04] align-top">
                    <td className="py-2.5">
                      <span className="block text-[#0f172a] font-medium">{r.label}</span>
                      <span className="block text-xs text-[#94a3b8]">{r.description}</span>
                    </td>
                    <td className="text-right pt-2">
                      {/* The standard price sits under the box, not inside it: as gray placeholder text it
                          made every untouched row look locked. */}
                      <div className="inline-flex items-center">
                        <span className="text-[#475569] mr-0.5">$</span>
                        <input type="text" inputMode="decimal" value={e.orgPrice}
                          onChange={(ev) => setEdit(r.serviceType, { orgPrice: ev.target.value })}
                          onBlur={(ev) => { const v = ev.target.value.trim(); if (v !== '' && Number.isFinite(parseFloat(v))) setEdit(r.serviceType, { orgPrice: parseFloat(v).toFixed(2) }); }}
                          aria-label={`${r.label} price`}
                          className="w-20 px-2 py-1 rounded-lg border border-black/[0.2] bg-white text-right text-sm text-[#0f172a] hover:border-black/[0.35] focus:outline-none focus:border-[#c41e3a] focus:ring-2 focus:ring-[#c41e3a]/20" />
                      </div>
                      <span className="block text-[11px] text-[#64748b] mt-1">Standard {money(r.standardBasePrice)}</span>
                    </td>
                    <td className="text-right pt-2">
                      <input type="number" min={0} max={100} step="0.5" value={e.discountPercent}
                        onChange={(ev) => setEdit(r.serviceType, { discountPercent: ev.target.value })}
                        aria-label={`${r.label} discount percent`}
                        className="w-16 px-2 py-1 rounded-lg border border-black/[0.2] bg-white text-right text-sm text-[#0f172a] hover:border-black/[0.35] focus:outline-none focus:border-[#c41e3a] focus:ring-2 focus:ring-[#c41e3a]/20" />
                    </td>
                    <td className={`text-right font-semibold whitespace-nowrap pt-3 ${custom ? 'text-[#c41e3a]' : 'text-[#0f172a]'}`}>{money(eff)}</td>
                    <td className="text-center pt-3">
                      {custom ? (
                        <input type="checkbox" checked={e.isActive}
                          onChange={(ev) => setEdit(r.serviceType, { isActive: ev.target.checked })} />
                      ) : (
                        <span className="text-[#94a3b8]" title="Set a price or discount to switch this on or off">—</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <div className="flex justify-end mt-4">
        <button onClick={handleSave} disabled={!dirty || saving}
          className="inline-flex items-center gap-1.5 text-sm font-semibold px-4 py-2 rounded-lg bg-[#c41e3a] text-white hover:bg-[#a51830] disabled:opacity-40">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save pricing
        </button>
      </div>
      </div>
      )}
    </div>
  );
};

export default ClientServicePricingSection;
